import React, { useState, useEffect, useCallback } from 'react';
import {
  FiPackage, FiRefreshCw, FiCheckCircle, FiAlertCircle,
  FiEye, FiEyeOff, FiSave, FiDatabase, FiWifi, FiWifiOff,
  FiCheck, FiChevronDown, FiChevronUp, FiInfo,
} from 'react-icons/fi';
import { getAdminOrganizations, AdminOrgRow } from '../../api/admin';
import {
  getEnterpriseOrgConfig, upsertEnterpriseOrgConfig,
  testEnterpriseStorageConfig, EnterpriseOrgConfig,
} from '../../api/admin';

/* ── helpers ──────────────────────────────────────────────────────────────────── */
const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });

/* ── Carte organisation ──────────────────────────────────────────────────────── */
const OrgCard: React.FC<{ org: AdminOrgRow }> = ({ org }) => {
  const [expanded,    setExpanded]    = useState(false);
  const [config,      setConfig]      = useState<EnterpriseOrgConfig | null>(null);
  const [loadingCfg,  setLoadingCfg]  = useState(false);
  const [form, setForm] = useState({
    storage_account_name:    '',
    storage_account_key:     '',
    bundles_container:       'execution-bundles',
    tfstate_container:       'tfstate',
    tfstate_resource_group:  '',
    is_provisioned:          false,
    notes:                   '',
  });
  const [showKey,    setShowKey]    = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [testing,    setTesting]    = useState(false);
  const [saveOk,     setSaveOk]     = useState(false);
  const [saveError,  setSaveError]  = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  /* Chargement config au premier clic */
  useEffect(() => {
    if (!expanded || config !== null || loadingCfg) return;
    setLoadingCfg(true);
    getEnterpriseOrgConfig(org.id)
      .then(({ config: c }) => {
        if (c) {
          setConfig(c);
          setForm({
            storage_account_name:   c.storage_account_name   || '',
            storage_account_key:    '',
            bundles_container:      c.bundles_container       || 'execution-bundles',
            tfstate_container:      c.tfstate_container       || 'tfstate',
            tfstate_resource_group: c.tfstate_resource_group  || '',
            is_provisioned:         c.is_provisioned,
            notes:                  c.notes || '',
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoadingCfg(false));
  }, [expanded]);

  const set = (key: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [key]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value }));

  const save = async () => {
    setSaving(true); setSaveOk(false); setSaveError(null);
    try {
      const payload: any = {
        storage_account_name:   form.storage_account_name   || null,
        bundles_container:      form.bundles_container,
        tfstate_container:      form.tfstate_container,
        tfstate_resource_group: form.tfstate_resource_group || null,
        is_provisioned:         form.is_provisioned,
        notes:                  form.notes || null,
      };
      if (form.storage_account_key) payload.storage_account_key = form.storage_account_key;
      const { config: updated } = await upsertEnterpriseOrgConfig(org.id, payload);
      setConfig(updated);
      setForm(f => ({ ...f, storage_account_key: '' }));
      setSaveOk(true);
      setTimeout(() => setSaveOk(false), 3000);
    } catch (err: any) {
      setSaveError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const res = await testEnterpriseStorageConfig(org.id);
      setTestResult(res);
    } catch { setTestResult({ success: false, message: 'Erreur réseau' }); }
    finally  { setTesting(false); }
  };

  const isConfigured = !!(config?.storage_account_name);
  const hasKey       = config?.has_key || !!form.storage_account_key;

  return (
    <div className={`bg-white rounded-xl border overflow-hidden transition ${
      isConfigured && config?.is_provisioned
        ? 'border-green-200'
        : isConfigured ? 'border-blue-200' : 'border-gray-200'
    }`}>
      {/* ── Header ── */}
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition text-left"
      >
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center font-bold text-sm ${
            isConfigured && config?.is_provisioned
              ? 'bg-green-100 text-green-700'
              : 'bg-purple-100 text-purple-700'
          }`}>
            {org.name?.[0]?.toUpperCase() || 'E'}
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-sm flex items-center gap-2">
              {org.name}
              {config?.is_provisioned && (
                <span className="flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                  <FiCheck size={10} /> Provisionné
                </span>
              )}
            </p>
            <p className="text-xs text-gray-500">{org.owner_email}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
            org.subscription_status === 'active'
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 text-gray-500'
          }`}>
            {org.subscription_status === 'active' ? 'Actif' : org.subscription_status}
          </span>
          {isConfigured ? (
            <span className="flex items-center gap-1 text-xs text-blue-600">
              <FiDatabase size={12} /> Configuré
            </span>
          ) : (
            <span className="text-xs text-gray-400">Non configuré</span>
          )}
          {expanded ? <FiChevronUp size={16} className="text-gray-400" /> : <FiChevronDown size={16} className="text-gray-400" />}
        </div>
      </button>

      {/* ── Contenu ── */}
      {expanded && (
        <div className="border-t border-gray-200 px-5 py-5 space-y-5">
          {loadingCfg && (
            <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
              <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
              Chargement de la configuration…
            </div>
          )}

          {/* Info rappel ADO */}
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600 flex items-start gap-2">
            <FiInfo size={13} className="text-slate-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-slate-700">Pipeline ADO — partagé (GenIaC-Entreprise)</p>
              <p className="mt-0.5">Org : <code className="bg-slate-100 px-1 rounded">GenIaC-Entreprise</code> · Projet : <code className="bg-slate-100 px-1 rounded">terraform-executions</code></p>
            </div>
          </div>

          {/* Résultat test */}
          {testResult && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs border ${
              testResult.success
                ? 'bg-green-50 border-green-200 text-green-700'
                : 'bg-red-50 border-red-200 text-red-700'
            }`}>
              {testResult.success ? <FiCheckCircle size={13} /> : <FiAlertCircle size={13} />}
              {testResult.message}
            </div>
          )}

          {/* Storage Azure */}
          <div>
            <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <FiDatabase size={14} className="text-purple-500" />
              Storage Azure dédié
              {config?.updated_at && (
                <span className="text-xs text-gray-400 font-normal ml-auto">
                  Mis à jour le {fmtDate(config.updated_at)}
                </span>
              )}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nom du Storage Account *</label>
                <input type="text" value={form.storage_account_name} onChange={set('storage_account_name')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="acmecorp-geniac-storage" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Resource Group</label>
                <input type="text" value={form.tfstate_resource_group} onChange={set('tfstate_resource_group')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="rg-acmecorp-infra" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Container bundles</label>
                <input type="text" value={form.bundles_container} onChange={set('bundles_container')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Container tfstate</label>
                <input type="text" value={form.tfstate_container} onChange={set('tfstate_container')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Storage Account Key
                  {config?.has_key && !form.storage_account_key && (
                    <span className="ml-2 text-green-600 font-normal text-xs">
                      <FiCheck size={10} className="inline" /> configurée — laisser vide pour conserver
                    </span>
                  )}
                </label>
                <div className="relative">
                  <input type={showKey ? 'text' : 'password'} value={form.storage_account_key}
                    onChange={set('storage_account_key')}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-400"
                    placeholder={config?.has_key ? '••••••••••••••••' : 'Clé d\'accès Azure Storage'} />
                  <button type="button" onClick={() => setShowKey(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showKey ? <FiEyeOff size={15} /> : <FiEye size={15} />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Provisionné + Notes */}
          <div className="flex items-center gap-2">
            <input type="checkbox" id={`prov-${org.id}`} checked={form.is_provisioned}
              onChange={e => setForm(f => ({ ...f, is_provisioned: e.target.checked }))}
              className="h-4 w-4 text-purple-600 border-gray-300 rounded accent-purple-600" />
            <label htmlFor={`prov-${org.id}`} className="text-sm text-gray-700">
              Marquer comme provisionné
            </label>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes internes</label>
            <textarea value={form.notes} onChange={set('notes')} rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
              placeholder="Ex: Onboardé le 01/03/2026 par AK · Ticket #1234" />
          </div>

          {saveError && (
            <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">
              <FiAlertCircle size={13} /> {saveError}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <button
              onClick={testConnection}
              disabled={testing || !hasKey || !form.storage_account_name}
              title={!hasKey ? 'Configurez une clé d\'abord' : 'Tester la connexion Azure Storage'}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 transition"
            >
              {testing
                ? <FiRefreshCw size={13} className="animate-spin" />
                : testResult?.success ? <FiWifi size={13} className="text-green-500" />
                : testResult ? <FiWifiOff size={13} className="text-red-500" />
                : <FiWifi size={13} />
              }
              Tester la connexion
            </button>
            <div className="flex items-center gap-3">
              {saveOk && (
                <span className="text-sm text-green-600 flex items-center gap-1">
                  <FiCheckCircle size={13} /> Sauvegardé
                </span>
              )}
              <button onClick={save} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg transition disabled:opacity-50">
                {saving ? <FiRefreshCw size={14} className="animate-spin" /> : <FiSave size={14} />}
                Sauvegarder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Page principale ─────────────────────────────────────────────────────────── */
const AdminEnterpriseOnboarding: React.FC = () => {
  const [orgs, setOrgs]       = useState<AdminOrgRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await getAdminOrganizations({ page: 1, limit: 100 });
      setOrgs((data.organizations as AdminOrgRow[]).filter(
        o => o.plan?.toLowerCase() === 'entreprise'
      ));
    } catch { setError('Impossible de charger les organisations'); }
    finally   { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Onboarding Entreprise</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Configuration du storage Azure dédié par client ENTREPRISE
          </p>
        </div>
        <button onClick={load}
          className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition">
          <FiRefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Actualiser
        </button>
      </div>

      {/* Architecture reminder */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <p className="text-xs font-semibold text-blue-800 mb-2 flex items-center gap-1.5">
          <FiInfo size={13} /> Architecture ENTREPRISE
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-blue-700">
          <div className="flex items-start gap-2">
            <span className="text-blue-400 mt-0.5">◆</span>
            <div>
              <p className="font-medium">Pipeline ADO — Partagé</p>
              <p className="text-blue-500">Org GenIaC-Entreprise commune à tous les clients Entreprise</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-blue-600 mt-0.5">⭐</span>
            <div>
              <p className="font-medium">Storage Azure — Dédié par client</p>
              <p className="text-blue-500">Tfstate (dev/rec/prod) dans l'abonnement Azure du client</p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <FiAlertCircle size={15} /> {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400 gap-2">
          <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
          Chargement…
        </div>
      ) : orgs.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <FiPackage size={36} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">Aucune organisation ENTREPRISE</p>
          <p className="text-xs text-gray-400 mt-1">
            Les organisations sur le plan ENTREPRISE apparaîtront ici.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-gray-500">{orgs.length} organisation{orgs.length > 1 ? 's' : ''} ENTREPRISE</p>
          {orgs.map(org => <OrgCard key={org.id} org={org} />)}
        </div>
      )}
    </div>
  );
};

export default AdminEnterpriseOnboarding;
